import { Injectable } from '@angular/core';
import {
  formatDateForTranslation,
  formatDisplayDate,
  formatDisplayDateTime,
  formatLocalizedDate,
  formatRelativeDate,
  LOCALIZED_DATE_FORMATS,
} from '@app/helpers/date.helper';
import { DEFAULT_LANGUAGE } from '@app/translations/i18n/i18n.service';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';

/**
 * Servizio per la gestione delle date localizzate
 * Integra moment.js con il TranslateService per formattare le date nella lingua corrente
 */
@Injectable({
  providedIn: 'root',
})
export class DateLocalizationService {
  constructor(private translateService: TranslateService) {}

  /**
   * Ottiene la lingua corrente con fallback su italiano
   * @returns La lingua corrente o 'it_IT' come fallback
   */
  private getCurrentLanguage(): string {
    return this.translateService.currentLang || DEFAULT_LANGUAGE;
  }

  /**
   * Formatta una data usando moment.js con localizzazione automatica
   * @param date - La data da formattare (stringa, Date, o moment)
   * @param format - Il formato da utilizzare (default: DISPLAY)
   * @returns La data formattata nella lingua corrente
   */
  formatDate(
    date: string | Date | moment.Moment,
    format: string = LOCALIZED_DATE_FORMATS.DISPLAY
  ): string {
    return formatLocalizedDate(date, format, this.translateService);
  }

  /**
   * Formatta una data per il display con localizzazione automatica
   * @param date - La data da formattare
   * @returns La data formattata per il display (DD/MM/YYYY o formato locale)
   */
  formatDisplayDate(date: string | Date | moment.Moment): string {
    return formatDisplayDate(date, this.translateService);
  }

  /**
   * Formatta una data con ora per il display con localizzazione automatica
   * @param date - La data da formattare
   * @returns La data e ora formattata per il display
   */
  formatDisplayDateTime(date: string | Date | moment.Moment): string {
    return formatDisplayDateTime(date, this.translateService);
  }

  /**
   * Formatta una data per le traduzioni con il translateService
   * @param date - La data da formattare
   * @param format - Il formato da utilizzare (default: DISPLAY)
   * @returns La data formattata nella lingua corrente
   */
  formatDateForTranslation(
    date: string | Date | moment.Moment,
    format: string = LOCALIZED_DATE_FORMATS.DISPLAY
  ): string {
    return formatDateForTranslation(date, this.translateService, format);
  }

  /**
   * Formatta una data relativa (es. "2 giorni fa", "ieri", "oggi")
   * @param date - La data da formattare
   * @returns La data relativa formattata nella lingua corrente
   */
  formatRelativeDate(date: string | Date | moment.Moment): string {
    return formatRelativeDate(date, this.translateService);
  }

  /**
   * Formatta una data per i tooltip e messaggi di traduzione
   * @param date - La data da formattare
   * @returns La data formattata per i tooltip
   */
  formatDateForTooltip(date: string | Date | moment.Moment): string {
    return this.formatDate(date, LOCALIZED_DATE_FORMATS.DISPLAY);
  }

  /**
   * Formatta una data per i log e report
   * @param date - La data da formattare
   * @returns La data formattata per i log
   */
  formatDateForLog(date: string | Date | moment.Moment): string {
    return this.formatDate(date, LOCALIZED_DATE_FORMATS.DISPLAY_WITH_TIME);
  }

  /**
   * Formatta una data per le API (formato ISO)
   * @param date - La data da formattare
   * @returns La data formattata in formato ISO
   */
  formatDateForAPI(date: string | Date | moment.Moment): string {
    return this.formatDate(date, LOCALIZED_DATE_FORMATS.ISO);
  }

  /**
   * Formatta una data per i filtri di ricerca
   * @param date - La data da formattare
   * @returns La data formattata per i filtri
   */
  formatDateForFilter(date: string | Date | moment.Moment): string {
    return this.formatDate(date, LOCALIZED_DATE_FORMATS.ISO);
  }

  /**
   * Formatta una data in formato compatto (DD MMMM YYYY)
   * @param date - La data da formattare
   * @returns La data formattata in formato compatto (es. 01 Dicembre 2024)
   */
  formatCompactDate(date: string | Date | moment.Moment): string {
    return this.formatDate(date, LOCALIZED_DATE_FORMATS.COMPACT);
  }

  /**
   * Ottiene la data corrente formattata
   * @param format - Il formato da utilizzare (default: DISPLAY)
   * @returns La data corrente formattata
   */
  getCurrentDate(format: string = LOCALIZED_DATE_FORMATS.DISPLAY): string {
    return this.formatDate(moment(), format);
  }

  /**
   * Ottiene la data di ieri formattata
   * @param format - Il formato da utilizzare (default: DISPLAY)
   * @returns La data di ieri formattata
   */
  getYesterdayDate(format: string = LOCALIZED_DATE_FORMATS.DISPLAY): string {
    return this.formatDate(moment().subtract(1, 'day'), format);
  }

  /**
   * Ottiene la data di domani formattata
   * @param format - Il formato da utilizzare (default: DISPLAY)
   * @returns La data di domani formattata
   */
  getTomorrowDate(format: string = LOCALIZED_DATE_FORMATS.DISPLAY): string {
    return this.formatDate(moment().add(1, 'day'), format);
  }

  /**
   * Verifica se una data è oggi
   * @param date - La data da verificare
   * @returns True se la data è oggi
   */
  isToday(date: string | Date | moment.Moment): boolean {
    return moment(date).isSame(moment(), 'day');
  }

  /**
   * Verifica se una data è ieri
   * @param date - La data da verificare
   * @returns True se la data è ieri
   */
  isYesterday(date: string | Date | moment.Moment): boolean {
    return moment(date).isSame(moment().subtract(1, 'day'), 'day');
  }

  /**
   * Verifica se una data è domani
   * @param date - La data da verificare
   * @returns True se la data è domani
   */
  isTomorrow(date: string | Date | moment.Moment): boolean {
    return moment(date).isSame(moment().add(1, 'day'), 'day');
  }

  /**
   * Calcola la differenza in giorni tra due date
   * @param date1 - Prima data
   * @param date2 - Seconda data
   * @returns Differenza in giorni
   */
  getDaysDifference(
    date1: string | Date | moment.Moment,
    date2: string | Date | moment.Moment
  ): number {
    return moment(date1).diff(moment(date2), 'days');
  }

  /**
   * Verifica se una data è scaduta
   * @param date - La data da verificare
   * @returns True se la data è scaduta
   */
  isExpired(date: string | Date | moment.Moment): boolean {
    return moment(date).isBefore(moment(), 'day');
  }

  /**
   * Verifica se una data scade entro un certo numero di giorni
   * @param date - La data da verificare
   * @param days - Numero di giorni (default: 89)
   * @returns True se la data scade entro il numero di giorni specificato
   */
  expiresWithinDays(
    date: string | Date | moment.Moment,
    days: number = 89
  ): boolean {
    const expirationDate = moment(date);
    const warningDate = moment().add(days, 'days');
    return (
      expirationDate.isSameOrBefore(warningDate, 'day') && !this.isExpired(date)
    );
  }
}
